import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate } from 'react-router-dom';

import { Button } from '~components';
import ContainerHeader from '~components/ContainerHeader';
import Icon, { faEnvelope, faPlus, faUsers } from '~components/icon';
import { Row, SortState, TableView } from '~components/table-view';

import columns from '~care-workers/forms/userList';

import { getAll } from '~care-workers/actions';
import { setTitle } from '~main/actions/login';

import { clearUsers } from '~care-workers/reducers/users';

import { selectUsers, selectUsersLoading, selectUsersTotal } from '~care-workers/selectors/users';

const Users: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [displayDeleted, setDisplayDeleted] = useState<boolean>(false);

  const initialSortState: SortState = [
    {
      id: 'date_last_login',
      direction: 'desc',
    },
  ];

  const [params, setParams] = useState({
    disabled: false,
    attributes: columns.attributes,
    associations: columns.associations,
    sort: initialSortState.map(({ id, direction }) => `${id}.${direction}`),
    limit: 100,
  });

  useEffect(() => {
    setTitle(`Users`);
    load();

    // clean component on unmount
    return () => {
      dispatch(clearUsers());
    };
  }, []);

  const load = async (loadParams: { [key: string]: any } = {}) => {
    const newParams = {
      ...params,
      ...loadParams,
    };
    dispatch(getAll({ ...newParams, care_worker: true }));
    setParams(newParams);
  };

  const sort = (sortState: SortState = []) => {
    load({
      sort: sortState.filter(({ direction }) => direction !== null).map(({ id, direction }) => `${id}.${direction}`),
    });
  };

  const toggleDisplayDeleted = async () => {
    await load({ disabled: !displayDeleted });
    setDisplayDeleted(!displayDeleted);
  };

  const openUser = useCallback((row: Row) => navigate(`/users/${row.id}`), [navigate]);
  const displayColumns = useMemo(() => columns.displayList(openUser), [openUser]);

  const users = useAppSelector(selectUsers);
  const usersTotal = useAppSelector(selectUsersTotal);
  const usersLoading = useAppSelector(selectUsersLoading);
  const isLoading = usersLoading === 'pending';

  return (
    <div className="p-2">
      <ContainerHeader icon={faUsers} iconSize="1x" className="info">
        User Admin
      </ContainerHeader>
      <div className="nav d-flex flex-row bg-light p-2 mt-2 mb-2">
        <Button
          disabled
          size="sm"
          color="success"
          className="ms-2"
          onClick={() => {
            // eslint-disable-next-line no-console
            console.log('Placeholder for nav to new user');
          }}
        >
          <Icon className="me-2" icon={faPlus} />
          Add New Care Worker
        </Button>
        <Button size="sm" color="warning" className="ms-2" onClick={() => toggleDisplayDeleted()}>
          View {displayDeleted ? 'Active' : 'Deleted'} Users
        </Button>

        <Button
          disabled
          size="sm"
          className="ms-2"
          onClick={() => {
            // eslint-disable-next-line no-console
            console.log('Placeholder for mail merge');
          }}
        >
          <Icon className="me-2" icon={faEnvelope} />
          Mail Merge
        </Button>
      </div>
      <TableView
        id="users"
        data={users as Row[]}
        columns={displayColumns}
        readonly
        striped
        isLoading={isLoading}
        emptyIcon={faUsers}
        emptyMessage={'No user admins found'}
        allowSort={true}
        initialSortState={initialSortState}
        onSort={sort}
        allowPagination={true}
        onLoadMore={() => load({ limit: params.limit + 50 })}
        showCount={true}
        totalCount={usersTotal}
      />
    </div>
  );
};

export default Users;
